import {ASINType, BUY_BOX_PAGE_TYPE, Offer, Product, StateEntity, TranslatorStringId} from "state/types";
import {Merchant} from "state/types/merchants";
import {VariationsState} from "state/types/variations";
import staticMediationV2Data from "../../components/widgets/medication-widget/cards/medication-card-v2/staticData.json";
import {findLeastStrengthDrugId} from "../../components/widgets/utils/med-widget-utils";
import {MedicationPrice} from "../types/MedicationWidget";

// Strings
interface StringsData {
    translatorStrings?: Record<TranslatorStringId, string>
}

let stringsDataHolder: StringsData | undefined;
const getStrings: () => Record<TranslatorStringId, string> = () => {
    if (!stringsDataHolder) {
        const rawStrings = document.getElementById("clinic-CDP-buy-box-react-root")?.dataset.strings ?? "{}";
        stringsDataHolder = JSON.parse(rawStrings) as StringsData;
    }
    return stringsDataHolder.translatorStrings ?? {};
}

const getObfuscatedMarketplaceId: string = "ATVPDKIKX0DER";

// Weblabs
type WeblabTreatment = 'C' | 'T1' | 'T2';
type WeblabTreatmentData = Record<string, WeblabTreatment>;

let weblabTreatmentDataHolder: WeblabTreatmentData | undefined;

const getWeblabTreatments: () => WeblabTreatmentData = () => {
    if (!weblabTreatmentDataHolder) {
        const rawStrings = document.getElementById('clinic-CDP-buy-box-react-root')?.dataset.weblab ?? '{}';
        weblabTreatmentDataHolder = JSON.parse(rawStrings) as WeblabTreatmentData;
    }
    return weblabTreatmentDataHolder ?? {};
};

// State
interface AvailableStatesData {
    states?: StateEntity[]
}

let availableStatesDataHolder: AvailableStatesData | undefined;
const getAvailableStates: () => StateEntity[] = () => {
    if (!availableStatesDataHolder) {
        const states = document.getElementById("clinic-CDP-buy-box-react-root")?.dataset.states ?? "{}";
        availableStatesDataHolder = JSON.parse(states) as AvailableStatesData;
    }
    const states = availableStatesDataHolder.states ?? [];
    // enable all states for sync project here on program level to avoid last minute code change for waitlist feature.
    // TODO entirely remove waitlist feature from this code package as sync project will support all sates + DC.
    // see https://t.corp.amazon.com/V892116666/communication
    return states.map(st => ({...st, launched: true}))
}

// Variations
type VariationsData = VariationsState
let variationsStatesHolder: VariationsData | undefined;
const getVariationsStates: () => VariationsState = () => {
    if (!variationsStatesHolder) {
        const variationsState = document.getElementById("clinic-CDP-buy-box-react-root")?.dataset.variations ?? "{}";
        variationsStatesHolder = JSON.parse(variationsState) as VariationsData;
        if (!variationsStatesHolder.variations) {
            variationsStatesHolder = {
                variations: []
            }
        }
    }
    return variationsStatesHolder;
}

interface OffersData {
    offers?: Offer[]
}

let offersDataHolder: OffersData | undefined;
const getOffers: () => Offer[] = () => {
    if (!offersDataHolder) {
        const offersString = document.getElementById("clinic-CDP-buy-box-react-root")?.dataset.offers || '{}';
        offersDataHolder = JSON.parse(offersString) as OffersData;
    }
    return offersDataHolder.offers ?? [];
}

interface MerchantsData {
    merchants?: Merchant[]
}

let merchantsDataHolder: MerchantsData | undefined;
const getMerchants: () => Merchant[] = () => {
    if (!merchantsDataHolder) {
        const dataString = document.getElementById("clinic-CDP-buy-box-react-root")?.dataset.merchants ?? '{}';
        merchantsDataHolder = JSON.parse(dataString) as MerchantsData;
    }
    return merchantsDataHolder.merchants ?? []
}

let productHolder: Product | undefined;
const getProduct: () => Product = () => {
    if (!productHolder) {
        const dataString = document.getElementById("clinic-CDP-buy-box-react-root")?.dataset.product ?? '{}';
        productHolder = JSON.parse(dataString) as Product;
        if (!productHolder.conditionName) {
            productHolder = {
                conditionName: ""
            }
        }
    }
    return productHolder;
}

let requestAsin: ASINType | undefined;
const getRequestAsin: () => ASINType = () => {
    if (!requestAsin) {
        const dataString = document.getElementById("clinic-CDP-buy-box-react-root")?.dataset.asin;
        requestAsin = dataString ?? getVariationsStates().defaultSelectedASIN ?? getVariationsStates().variations[0]?.asin ?? "FAKE_ASIN"
    }
    return requestAsin;
}

let pageType: BUY_BOX_PAGE_TYPE | undefined;
const getPageType: () => BUY_BOX_PAGE_TYPE = () => {
    if (!pageType) {
        const dataString = document.getElementById("clinic-CDP-buy-box-react-root")?.dataset.pagetype;
        pageType = BUY_BOX_PAGE_TYPE[dataString as keyof typeof BUY_BOX_PAGE_TYPE] ?? BUY_BOX_PAGE_TYPE.DETAIL_PAGE
    }
    return pageType;
}

let isMShopApp: boolean;
const getIsMShopApp: () => boolean = () => {
    if (isMShopApp === undefined) {
        const dataString = document.getElementById('clinic-CDP-buy-box-react-root')?.dataset?.ismshop?.toLowerCase();
        isMShopApp = dataString === "true";
    }
    return isMShopApp;
}

const getMedicationSummaryWidgetAttributes = () => {
    const medicationSummaries = document.getElementById("clinic-medication-summary-carousel-widget")?.dataset.widgetattr ?? "{}";
    return JSON.parse(medicationSummaries);
}

const getMedicationPriceEstimationWidgetAttributes = () => {
    // TODO:"clinic-medication-carousel-cards-widget" to be removed after FF launch 3 as carousel was just a style and we are using new token names,
    //  https://issues.amazon.com/issues/KATARA-SX-6393
    const medications = document.getElementById("clinic-medication-carousel-cards-widget")?.dataset.widgetattr ??
        document.getElementById("clinic-medication-price-estimation-widget")?.dataset.widgetattr ??
        "{}";
    return JSON.parse(medications);
}

let customerTestimonialHolder: any | undefined;
const getCustomerTestimonialWidgetAttributes = () => {
    if (!customerTestimonialHolder) {
        const data: any = JSON.parse(
            document.getElementById('clinic-customer-testimonial-widget')?.dataset.widgetattr ?? '{}'
        );
        if (data.testimonials) {
            customerTestimonialHolder = data;
        } else {
            customerTestimonialHolder = {
                testimonials: [],
            };
        }
    }
    return customerTestimonialHolder;
};

const getInitialMedicationPrice = (): MedicationPrice => {
    let medWidgetAttributes = getMedicationPriceEstimationWidgetAttributes();
    let medications;
    if (!!(medWidgetAttributes.models?.medicationsV3)) {
        // TODO: extract real price from medication widget attribute when dynamic pricing is done from mid layer.
        //  https://issues.amazon.com/issues/KATARA-SX-6249
        medications = medWidgetAttributes.models.medicationsV3;
    } else {
        medications = staticMediationV2Data;
    }
    let firstMedDrugIDToVariations = medications[0].drugIDToVariations;
    let firstMedicationOption = firstMedDrugIDToVariations[findLeastStrengthDrugId(firstMedDrugIDToVariations)].options[0];
    return {
        perPillPrimePrice: firstMedicationOption?.perPillPrimePrice,
        totalPrimePrice: firstMedicationOption?.totalPrimePrice,
        totalRetailPrice: firstMedicationOption?.totalRetailPrice,
    }
}

const InitialContext = {
    getStrings,
    getAvailableStates,
    getVariationsStates,
    getOffers,
    getMerchants,
    getProduct,
    getRequestAsin,
    getWeblabTreatments,
    getPageType,
    getIsMShopApp,
    getObfuscatedMarketplaceId,
    getMedicationSummaryWidgetAttributes,
    getMedicationPriceEstimationWidgetAttributes,
    getInitialMedicationPrice,
    getCustomerTestimonialWidgetAttributes
}
export default InitialContext;
